import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { GoogleMap, useJsApiLoader, Polyline } from '@react-google-maps/api';
import { X, Phone, Navigation, Loader2, Search } from 'lucide-react';
import { googleMapsLoaderConfig } from '../../config/googleMapsConfig';
import { uberMapStyle } from '../../styles/mapStyle';
import { computeRoute } from '../../utils/routesApi';
import CommandCenterMapLegend from './CommandCenterMapLegend';
import ambulancePng from '../../svg/Rectangle.png';
import commandCenterService from '../../services/commandCenterService';
import CommandCenterWaitingForDriverPopup from './CommandCenterWaitingForDriverPopup';
import { makePinSvg } from '../../utils/mapMarkers';

// Maps a real backend candidate to the shape this modal's list/map already render.
const mapCandidateToDriver = (c) => ({
  id: c.vehicleId,
  driverId: c.driverId,
  vehicleId: c.vehicleId,
  name: c.driverName,
  phone: c.driverPhone,
  vehiclePlate: c.plateNumber,
  vehicleType: c.vehicleTypeLabel,
  distanceKm: c.distanceKm,
  etaMinutes: c.etaMinutes,
  location: c.location,
});

const DRIVER_ICON_SIZE = { normal: 44, selected: 72, current: 48 };

const CHANGE_REASONS = [
  'Driver unavailable',
  'Vehicle breakdown',
  'Better ETA available',
  'Patient/Hospital request',
  'Other',
];

const CommandCenterAssignDriverModal = ({ trip, onClose, onAssigned, onError, mode = 'assign', currentDriver = null }) => {
  const { isLoaded } = useJsApiLoader({ ...googleMapsLoaderConfig });
  const [selectedDriverId, setSelectedDriverId] = useState(null);
  const [map, setMap] = useState(null);
  const [routePath, setRoutePath] = useState([]);
  const [reason, setReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [drivers, setDrivers] = useState([]);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [waitingForDriver, setWaitingForDriver] = useState(null); // null | { driver }
  const [searchQuery, setSearchQuery] = useState('');
  const pickupMarkerRef = useRef(null);
  const dropoffMarkerRef = useRef(null);
  const driverMarkersRef = useRef({});
  const currentDriverMarkerRef = useRef(null);

  const isChangeMode = mode === 'change';
  const requiresReason = isChangeMode && !!currentDriver;
  const finalReason = reason === 'Other' ? customReason.trim() : reason;

  const requestedVehicleTypes = useMemo(() => {
    if (Array.isArray(trip?.requestedVehicleTypes) && trip.requestedVehicleTypes.length > 0) {
      return trip.requestedVehicleTypes;
    }
    if (trip?.service && trip.service !== '—') {
      return [trip.service];
    }
    return [];
  }, [trip?.requestedVehicleTypes, trip?.service]);

  const requestedVehicleTypeLabel = requestedVehicleTypes.join(', ');

  // Reset selection/reason and load real nearby candidates whenever a fresh
  // trip is opened.
  useEffect(() => {
    setSelectedDriverId(null);
    setReason('');
    setCustomReason('');
    setDrivers([]);
    setSearchQuery('');
    // No pickup coordinates means no nearby-driver search is possible (the
    // backend requires them) — the render below already shows a dedicated
    // "no pickup location" screen for this case instead of the driver list.
    if (!trip?.id || !trip?.pickup) return;
    setLoadingDrivers(true);
    // Capped to the default page size server-side — this map-heavy modal
    // only ever needs a manageable shortlist, not the whole fleet.
    commandCenterService.getCandidates(trip.id, {
      maxEtaMinutes: 60,
      partnerFirst: true,
      vehicleTypeNames: requestedVehicleTypes.length > 0 ? requestedVehicleTypes : undefined,
    })
      .then((result) => setDrivers((result.data?.candidates || []).map(mapCandidateToDriver)))
      .catch((error) => onError?.(error.message || 'Failed to load nearby drivers'))
      .finally(() => setLoadingDrivers(false));
  }, [trip?.id, requestedVehicleTypes]);

  // Filter drivers by search query across multiple dimensions:
  // - Driver name (e.g. Sanjana, Driver 81)
  // - Phone number (e.g. 9593164305, 9000000240)
  // - Vehicle registration / plate number (e.g. KA19CE2312, MH12AB1002, matching with/without hyphens & spaces)
  // - Vehicle type / category (e.g. Advanced Life Support, ALS, BLS)
  // - Driver ID / Vehicle ID
  const filteredDrivers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return drivers;

    const cleanQuery = query.replace(/[^a-z0-9]/g, '');

    return drivers.filter((driver) => {
      // 1. Driver Name
      const name = (driver.name || '').toLowerCase();
      if (name.includes(query)) return true;

      // 2. Phone Number
      const phone = (driver.phone || '').toString();
      const cleanPhone = phone.replace(/[^0-9]/g, '');
      if (phone.includes(query) || (cleanQuery && cleanPhone.includes(cleanQuery))) return true;

      // 3. Vehicle Plate Number (handles both formatted and unformatted e.g. "KA19" or "KA-19")
      const plate = (driver.vehiclePlate || '').toLowerCase();
      const cleanPlate = plate.replace(/[^a-z0-9]/g, '');
      if (plate.includes(query) || (cleanQuery && cleanPlate.includes(cleanQuery))) return true;

      // 4. Vehicle Type / Category
      const vehicleType = (driver.vehicleType || '').toLowerCase();
      if (vehicleType.includes(query)) return true;

      // 5. Driver ID or Vehicle ID
      const dId = (driver.driverId || driver.id || '').toString().toLowerCase();
      if (dId.includes(query)) return true;

      return false;
    });
  }, [drivers, searchQuery]);

  const selectedDriver = filteredDrivers.find((d) => d.id === selectedDriverId) || null;

  // Clear selection whenever the filtered list no longer contains the currently
  // selected driver (e.g. user typed a search query that excludes them), preventing
  // a hidden driver from remaining selected or being assigned.
  useEffect(() => {
    if (selectedDriverId && !filteredDrivers.some((d) => d.id === selectedDriverId)) {
      setSelectedDriverId(null);
    }
  }, [filteredDrivers, selectedDriverId]);

  const bounds = useMemo(() => {
    if (!trip?.pickup) return null;
    const lats = [trip.pickup.latitude, ...(trip.dropoff ? [trip.dropoff.latitude] : []), ...drivers.map((d) => d.location.latitude)];
    const lngs = [trip.pickup.longitude, ...(trip.dropoff ? [trip.dropoff.longitude] : []), ...drivers.map((d) => d.location.longitude)];
    if (currentDriver?.location) {
      lats.push(currentDriver.location.latitude);
      lngs.push(currentDriver.location.longitude);
    }
    return {
      north: Math.max(...lats),
      south: Math.min(...lats),
      east: Math.max(...lngs),
      west: Math.min(...lngs),
    };
  }, [trip, drivers, currentDriver]);

  const onMapLoad = useCallback((mapInstance) => {
    setMap(mapInstance);
    if (bounds && window.google?.maps) {
      const b = new window.google.maps.LatLngBounds(
        { lat: bounds.south, lng: bounds.west },
        { lat: bounds.north, lng: bounds.east }
      );
      mapInstance.fitBounds(b, { padding: 80 });
    }
  }, [bounds]);

  // Pickup/dropoff markers: imperative native markers, same technique
  // TrackingMap.jsx uses for its pickup/dropoff pins ("guaranteed rendering").
  // trip.pickup can genuinely be null — bookings from other modules (e.g. a
  // Hospital-created scheduled booking entered as free-text address) can
  // have no geocoded coordinates at all (see CommandCenterTripMap.jsx's own
  // "No coordinates recorded" handling for the same case).
  useEffect(() => {
    if (!map || !window.google?.maps || !trip?.pickup) return undefined;

    // Pickup is blue pin; drop-off is red pin (matching Google Maps pin style)
    pickupMarkerRef.current = new window.google.maps.Marker({
      map,
      position: { lat: trip.pickup.latitude, lng: trip.pickup.longitude },
      title: `Pickup: ${trip.pickup.address || ''}`,
      icon: {
        url: makePinSvg('blue'),
        scaledSize: new window.google.maps.Size(26, 38),
        anchor: new window.google.maps.Point(13, 38),
      },
      zIndex: 900,
      optimized: false,
    });

    if (trip.dropoff) {
      dropoffMarkerRef.current = new window.google.maps.Marker({
        map,
        position: { lat: trip.dropoff.latitude, lng: trip.dropoff.longitude },
        title: `Drop-off: ${trip.dropoff.address || ''}`,
        icon: {
          url: makePinSvg('red'),
          scaledSize: new window.google.maps.Size(26, 38),
          anchor: new window.google.maps.Point(13, 38),
        },
        zIndex: 900,
        optimized: false,
      });
    }

    return () => {
      pickupMarkerRef.current?.setMap(null);
      dropoffMarkerRef.current?.setMap(null);
      pickupMarkerRef.current = null;
      dropoffMarkerRef.current = null;
    };
  }, [map, trip]);

  // Driver markers: also imperative (same reliability reasoning as pickup/dropoff),
  // created once per driver list, with a click listener to select them.
  useEffect(() => {
    if (!map || !window.google?.maps) return undefined;

    const markers = drivers.map((driver) => {
      const marker = new window.google.maps.Marker({
        map,
        position: { lat: driver.location.latitude, lng: driver.location.longitude },
        title: driver.name,
        icon: {
          url: ambulancePng,
          scaledSize: new window.google.maps.Size(DRIVER_ICON_SIZE.normal, DRIVER_ICON_SIZE.normal),
          anchor: new window.google.maps.Point(DRIVER_ICON_SIZE.normal / 2, DRIVER_ICON_SIZE.normal / 2),
        },
        zIndex: 920,
        optimized: false,
      });
      marker.addListener('click', () => setSelectedDriverId(driver.id));
      return [driver.id, marker];
    });

    driverMarkersRef.current = Object.fromEntries(markers);

    return () => {
      markers.forEach(([, marker]) => marker.setMap(null));
      driverMarkersRef.current = {};
    };
  }, [map, drivers]);

  // Currently assigned driver (Change mode only) — kept as its own marker,
  // separate from the reassignment candidates, always shown at full opacity.
  useEffect(() => {
    if (!map || !window.google?.maps || !currentDriver?.location) return undefined;

    currentDriverMarkerRef.current = new window.google.maps.Marker({
      map,
      position: { lat: currentDriver.location.latitude, lng: currentDriver.location.longitude },
      title: `Currently assigned: ${currentDriver.name}`,
      icon: {
        url: ambulancePng,
        scaledSize: new window.google.maps.Size(DRIVER_ICON_SIZE.current, DRIVER_ICON_SIZE.current),
        anchor: new window.google.maps.Point(DRIVER_ICON_SIZE.current / 2, DRIVER_ICON_SIZE.current / 2),
      },
      label: { text: 'CURRENT', color: '#ffffff', fontSize: '9px', fontWeight: '700' },
      zIndex: 940,
      optimized: false,
    });

    return () => {
      currentDriverMarkerRef.current?.setMap(null);
      currentDriverMarkerRef.current = null;
    };
  }, [map, currentDriver]);

  // Update icon size/opacity and visibility in place when the selection or search filter changes.
  useEffect(() => {
    if (!window.google?.maps) return;
    const matchingDriverIds = new Set(filteredDrivers.map((d) => d.id));
    const hasSearch = searchQuery.trim().length > 0;

    Object.entries(driverMarkersRef.current).forEach(([driverId, marker]) => {
      const isSelected = driverId === selectedDriverId;
      const isMatch = matchingDriverIds.has(driverId);
      const size = isSelected ? DRIVER_ICON_SIZE.selected : DRIVER_ICON_SIZE.normal;
      marker.setIcon({
        url: ambulancePng,
        scaledSize: new window.google.maps.Size(size, size),
        anchor: new window.google.maps.Point(size / 2, size / 2),
      });

      if (hasSearch) {
        marker.setVisible(isMatch);
        marker.setOpacity(isSelected ? 1 : isMatch ? 0.95 : 0.2);
        marker.setZIndex(isSelected ? 960 : isMatch ? 940 : 900);
      } else {
        marker.setVisible(true);
        marker.setOpacity(isSelected || !selectedDriverId ? 1 : 0.55);
        marker.setZIndex(isSelected ? 950 : 920);
      }
    });
  }, [selectedDriverId, filteredDrivers, searchQuery]);

  // Route between pickup and dropoff — same Routes API helper + straight-line
  // fallback TrackingMap.jsx uses, rendered as a JSX Polyline (proven reliable
  // for lines in this app, unlike JSX Markers).
  useEffect(() => {
    if (!isLoaded || !trip?.pickup || !trip?.dropoff) return;

    const origin = { lat: trip.pickup.latitude, lng: trip.pickup.longitude };
    const destination = { lat: trip.dropoff.latitude, lng: trip.dropoff.longitude };
    const straightLine = [origin, destination];
    let cancelled = false;

    setRoutePath([]);
    computeRoute(origin, destination)
      .then(({ coordinates }) => {
        if (cancelled) return;
        setRoutePath(coordinates.length > 0 ? coordinates : straightLine);
      })
      .catch(() => {
        if (!cancelled) setRoutePath(straightLine);
      });

    return () => { cancelled = true; };
  }, [isLoaded, trip]);

  if (!trip) return null;

  // Some bookings (e.g. Hospital-created ones entered as free-text address,
  // no map pin) have no geocoded pickup at all — same case
  // CommandCenterTripMap.jsx already shows a "no coordinates" message for.
  // There's no nearby-driver search possible without a pickup point (the
  // backend's own findCandidates requires it), so say so plainly instead of
  // rendering a map with nothing on it and an empty, misleading driver list.
  if (!trip.pickup) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
        onClick={onClose}
      >
        <div
          className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 text-center space-y-3"
          onClick={(e) => e.stopPropagation()}
        >
          <h3 className="text-lg font-semibold text-gray-900">No pickup location on file</h3>
          <p className="text-sm text-gray-500">
            Booking #{trip.id} has no recorded pickup coordinates, so nearby drivers can't be ranked for it.
            Use Edit to set a pickup location first.
          </p>
          <button
            onClick={onClose}
            className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={() => { if (!submitting) onClose(); }}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-5xl h-[640px] max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-lg font-semibold text-gray-900">
                {isChangeMode ? 'Change Driver' : 'Assign Driver'} <span className="text-primary">#{trip.id}</span>
              </h3>
              {requestedVehicleTypeLabel && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                  {requestedVehicleTypeLabel}
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              {isChangeMode && currentDriver
                ? 'Pick a replacement driver and give a reason for the change.'
                : requestedVehicleTypeLabel
                  ? `Nearest available ${requestedVehicleTypeLabel} drivers to the pickup location.`
                  : 'Nearest available drivers to the pickup location.'}
            </p>
          </div>
          <button
            onClick={() => { if (!submitting) onClose(); }}
            disabled={submitting}
            className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-200 text-gray-500 hover:bg-gray-50 shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 flex flex-col sm:flex-row overflow-hidden">
          {/* Driver list sidebar */}
          <div className="w-full sm:w-80 shrink-0 max-h-64 sm:max-h-none border-b sm:border-b-0 sm:border-r border-gray-100 flex flex-col bg-white">
            {isChangeMode && (
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 shrink-0">
                <div className="text-[10px] font-semibold tracking-wide text-gray-400 mb-1.5">CURRENTLY ASSIGNED</div>
                {currentDriver ? (
                  <>
                    <div className="font-medium text-gray-900">{currentDriver.name}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{currentDriver.vehiclePlate} &middot; {currentDriver.vehicleType}</div>
                    {currentDriver.phone ? (
                      <a
                        href={`tel:${currentDriver.phone}`}
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1 text-xs text-blue-600 font-medium hover:underline mt-1 cursor-pointer"
                        title={`Call driver: ${currentDriver.phone}`}
                      >
                        <Phone className="w-3 h-3 shrink-0" />
                        <span>{currentDriver.phone}</span>
                      </a>
                    ) : (
                      <span className="text-xs text-gray-400 mt-1">No phone</span>
                    )}
                  </>
                ) : (
                  <div className="text-sm text-gray-400">No driver assigned yet</div>
                )}
              </div>
            )}

            {/* Driver Search Input Field */}
            <div className="p-3 border-b border-gray-100 bg-white shrink-0">
              <div className="relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search name, phone, plate..."
                  className="w-full pl-9 pr-8 py-2 rounded-xl border border-gray-200 text-xs text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all bg-gray-50/70 hover:bg-white"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="w-5 h-5 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 absolute right-2.5 top-1/2 -translate-y-1/2 transition-colors cursor-pointer"
                    title="Clear search"
                  >
                    <X className="w-3 h-3 stroke-[2.5]" />
                  </button>
                )}
              </div>

              {/* Live search results counter */}
              {!loadingDrivers && drivers.length > 0 && (
                <div className="flex items-center justify-between text-[11px] text-gray-400 mt-1.5 px-0.5">
                  <span>
                    {searchQuery.trim()
                      ? `Found ${filteredDrivers.length} of ${drivers.length} drivers`
                      : `${drivers.length} nearby drivers`}
                  </span>
                  {searchQuery.trim() && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      className="text-primary hover:underline font-medium cursor-pointer"
                    >
                      Clear
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Scrollable drivers list */}
            <div className="flex-1 overflow-y-auto">
              {loadingDrivers && (
                <div className="px-4 py-6 flex items-center justify-center gap-2 text-sm text-gray-400">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading nearby drivers&hellip;
                </div>
              )}

              {!loadingDrivers && drivers.length === 0 && (
                <div className="px-4 py-6 text-center text-sm text-gray-400">
                  {requestedVehicleTypeLabel
                    ? `No available ${requestedVehicleTypeLabel} ambulances found nearby.`
                    : 'No available ambulances found nearby.'}
                </div>
              )}

              {!loadingDrivers && drivers.length > 0 && filteredDrivers.length === 0 && (
                <div className="px-4 py-8 text-center space-y-2">
                  <div className="w-9 h-9 mx-auto rounded-full bg-gray-100 flex items-center justify-center text-gray-400">
                    <Search className="w-4 h-4" />
                  </div>
                  <p className="text-xs font-semibold text-gray-700">No matching drivers</p>
                  <p className="text-[11px] text-gray-400 max-w-[210px] mx-auto leading-relaxed">
                    No driver matches &ldquo;{searchQuery}&rdquo;. Try searching by name, phone number, or vehicle plate.
                  </p>
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline pt-1 cursor-pointer"
                  >
                    Clear search
                  </button>
                </div>
              )}

              {filteredDrivers.map((driver) => {
                const isSelected = driver.id === selectedDriverId;
                return (
                  <button
                    key={driver.id}
                    onClick={() => setSelectedDriverId(driver.id)}
                    className={`w-full text-left px-4 py-3 border-b border-gray-50 transition-colors cursor-pointer ${
                      isSelected ? 'bg-primary/5' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`font-medium ${isSelected ? 'text-primary' : 'text-gray-900'}`}>{driver.name}</span>
                      {isSelected && (
                        <span className="w-2 h-2 rounded-full bg-primary shrink-0" />
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{driver.vehiclePlate} &middot; {driver.vehicleType}</div>
                    <div className="flex items-center gap-3 text-xs text-gray-500 mt-1.5">
                      <span className="inline-flex items-center gap-1">
                        <Navigation className="w-3 h-3" />
                        {driver.distanceKm.toFixed(1)} km away
                      </span>
                      <span>~{driver.etaMinutes} min</span>
                    </div>
                    {driver.phone ? (
                      <a
                        href={`tel:${driver.phone}`}
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1 text-xs text-blue-600 font-medium hover:underline mt-1 cursor-pointer"
                        title={`Call driver: ${driver.phone}`}
                      >
                        <Phone className="w-3 h-3 shrink-0" />
                        <span>{driver.phone}</span>
                      </a>
                    ) : (
                      <span className="text-xs text-gray-400 mt-1">No phone</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Map */}
          <div className="flex-1 flex flex-col">
            <div className="flex-1 relative">
              {isLoaded ? (
                <GoogleMap
                  mapContainerStyle={{ width: '100%', height: '100%' }}
                  onLoad={onMapLoad}
                  options={{
                    zoomControl: true,
                    streetViewControl: false,
                    mapTypeControl: false,
                    fullscreenControl: false,
                    styles: uberMapStyle,
                  }}
                >
                  {/* Pickup/dropoff/driver markers are added imperatively (see effects above) */}

                  {routePath.length > 0 && window.google?.maps && (
                    <>
                      {/* Glow/Shadow line */}
                      <Polyline
                        path={routePath}
                        options={{
                          strokeColor: '#1E40AF',
                          strokeOpacity: 0.25,
                          strokeWeight: 10,
                          geodesic: true,
                          zIndex: 895,
                        }}
                      />
                      {/* Main Blue Route with Forward Direction Arrows */}
                      <Polyline
                        path={routePath}
                        options={{
                          strokeColor: '#1A73E8',
                          strokeOpacity: 1,
                          strokeWeight: 5,
                          geodesic: true,
                          zIndex: 898,
                          icons: [
                            {
                              icon: {
                                path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                                fillColor: '#1A73E8',
                                fillOpacity: 1,
                                strokeColor: '#FFFFFF',
                                strokeWeight: 1.5,
                                scale: 3,
                              },
                              offset: '25px',
                              repeat: '65px',
                            },
                          ],
                        }}
                      />
                    </>
                  )}
                </GoogleMap>
              ) : (
                <div className="w-full h-full bg-gray-100" />
              )}
            </div>
            <CommandCenterMapLegend />
          </div>
        </div>

        {requiresReason && selectedDriver && (
          <div className="px-6 py-4 border-t border-gray-100 shrink-0 bg-gray-50/60 space-y-2">
            <label className="text-xs font-semibold text-gray-500">Reason for changing the driver</label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 rounded-md border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            >
              <option value="">Select a reason&hellip;</option>
              {CHANGE_REASONS.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
            {reason === 'Other' && (
              <textarea
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                placeholder="Describe the reason…"
                rows={2}
                className="w-full px-3 py-2 rounded-md border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
              />
            )}
          </div>
        )}

        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 shrink-0">
          <span className="text-sm text-gray-500">
            {selectedDriver ? `Selected: ${selectedDriver.name} (${selectedDriver.distanceKm.toFixed(1)} km away)` : 'Select a driver from the list or map'}
          </span>
          <button
            disabled={!selectedDriver || (requiresReason && !finalReason) || submitting}
            onClick={async () => {
              if (!selectedDriver || (requiresReason && !finalReason)) return;
              if (trip?.when?.at) {
                const tripTime = new Date(trip.when.at).getTime();
                if (trip.when.label === 'Scheduled' && tripTime < Date.now()) {
                  onError?.('Driver cannot be assigned to a past-dated booking.');
                  return;
                }
                if (Date.now() - tripTime > 24 * 60 * 60 * 1000) {
                  onError?.('Driver cannot be assigned to a past-dated booking.');
                  return;
                }
              }
              setSubmitting(true);
              try {
                if (requiresReason) {
                  // Change Driver is a direct, dispatcher-driven correction
                  // (reason required) — stays instant/authoritative, unlike
                  // a fresh 'app' assignment below.
                  await commandCenterService.reassignDriver(trip.id, {
                    driverId: selectedDriver.driverId,
                    vehicleId: selectedDriver.vehicleId,
                    reason: finalReason,
                  });
                  onAssigned?.(selectedDriver, finalReason);
                } else {
                  // Negotiable — the driver still has to accept via their
                  // own app, so don't report success yet; wait and watch.
                  await commandCenterService.assignDriver(trip.id, {
                    driverId: selectedDriver.driverId,
                    vehicleId: selectedDriver.vehicleId,
                    notifyMethod: 'app',
                  });
                  setWaitingForDriver({ driver: selectedDriver });
                }
              } catch (error) {
                onError?.(error.message || 'Failed to assign driver');
              } finally {
                setSubmitting(false);
              }
            }}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedDriver && !(requiresReason && !finalReason) && !submitting
                ? 'bg-primary hover:bg-primary/90 text-primary-foreground'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {selectedDriver
              ? (isChangeMode ? `Change to ${selectedDriver.name}` : `Assign to ${selectedDriver.name}`)
              : (isChangeMode ? 'Change Driver' : 'Assign')}
          </button>
        </div>
      </div>
    </div>

    {waitingForDriver && (
      <CommandCenterWaitingForDriverPopup
        bookingId={trip.id}
        driver={waitingForDriver.driver}
        onClose={() => setWaitingForDriver(null)}
        onConfirmed={() => {
          onAssigned?.(waitingForDriver.driver, undefined);
          setWaitingForDriver(null);
        }}
        onDeclined={() => {
          onError?.(`${waitingForDriver.driver.name} declined or didn't respond — please choose another driver`);
        }}
      />
    )}
    </>
  );
};

export default CommandCenterAssignDriverModal;